// src/pages/Collections/Collections.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getCollectionProgress } from '../../services/collectionsService';
import { listPublicCollections } from '../../services/publicCollectionsService';
import { useAuth } from '../../context/AuthContext';
import ProgressiveCoinReveal from '../../components/ProgressiveCoinReveal/ProgressiveCoinReveal';
import styles from './Collections.module.css';
import { SignatureBadge } from '../../components/CoinMedia/CoinMedia';
import { resolveCoinMedia } from '../../utils/coinMedia';
import { SeasonalBadge, seasonalProgressCopy } from '../../components/SeasonalAvailability/SeasonalAvailability';
import { completionNumbers, progressLabel, requirementLabel, unlockFraction } from '@aluvo/constants/src/collectionCompletion';
import { CollectionCardCollectors } from '../../components/CollectorStats/CollectorStats';

const FILTERS = [
    { label: 'All',         value: 'all' },
    { label: 'In Progress', value: 'inprogress' },
    { label: 'Completed',   value: 'completed' },
];

export default function Collections() {
    const { user, loading: authLoading } = useAuth();

    // Wait for auth state to settle before deciding which branch to render
    if (authLoading) {
        return (
            <div className={styles.page}>
                <div className={styles.container}>
                    <p className={styles.loading}>Loading…</p>
                </div>
            </div>
        );
    }

    return user ? <AuthedCollections /> : <PublicCollections />;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Logged-IN branch — existing behaviour, untouched
───────────────────────────────────────────────────────────────────────────── */
function AuthedCollections() {
    const navigate = useNavigate();
    const [collections, setCollections] = useState([]);
    const [loading, setLoading]         = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilter, setActiveFilter] = useState('all');

    useEffect(() => {
        loadCollections();
    }, []);

    async function loadCollections() {
        try {
            setLoading(true);
            const data = await getCollectionProgress();
            setCollections(data);
        } catch (err) {
            console.error('Failed to load collections:', err);
        } finally {
            setLoading(false);
        }
    }

    const filteredCollections = useMemo(() => {
        return collections.filter((c) => {
            const matchesSearch =
                searchQuery.trim() === '' ||
                c.name.toLowerCase().includes(searchQuery.toLowerCase());

            const matchesFilter =
                activeFilter === 'all' ||
                (activeFilter === 'completed'  && c.completed) ||
                (activeFilter === 'inprogress' && !c.completed);

            return matchesSearch && matchesFilter;
        });
    }, [collections, searchQuery, activeFilter]);

    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.container}>
                    <p className={styles.loading}>Loading collections…</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                <h1 className={styles.title}>Legendary Collections</h1>
                <p className={styles.subtitle}>
                    Complete collections to earn exclusive Collection coins
                </p>

                {/* ── Search + Filter pills ── */}
                <div className={styles.filterBar}>
                    <div className={styles.searchWrap}>
                        <span className={styles.searchIcon}>🔍</span>
                        <input
                            type="text"
                            className={styles.searchInput}
                            placeholder="Search collections…"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button
                                className={styles.searchClear}
                                onClick={() => setSearchQuery('')}
                                aria-label="Clear search"
                            >
                                ✕
                            </button>
                        )}
                    </div>

                    <div className={styles.pills}>
                        {FILTERS.map((f) => (
                            <button
                                key={f.value}
                                className={`${styles.pill} ${activeFilter === f.value ? styles.pillActive : ''}`}
                                onClick={() => setActiveFilter(f.value)}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── Grid ── */}
                {filteredCollections.length === 0 ? (
                    <p className={styles.emptyText}>
                        {collections.length === 0
                            ? 'Start verifying movies and shows to unlock Legendary collection coins.'
                            : 'No collections match your search.'}
                    </p>
                ) : (
                    <div className={styles.grid}>
                        {filteredCollections.map((collection) => (
                            <CollectionCard
                                key={collection.id}
                                collection={collection}
                                onClick={() => navigate(`/app/collections/${collection.slug}`)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function CollectionCard({ collection, onClick }) {
    // Progress toward UNLOCKING (the threshold for "any N of M" collections).
    const progressPercent = unlockFraction(collection) * 100;
    const { towardRequired, required } = completionNumbers(collection);
    const requirement = requirementLabel(collection);
    const previewPosters = collection.items.slice(0, 5).filter((i) => i.posterUrl);
    const nextTitle = collection.items.find((i) => !i.verified)?.title;
    const seasonalCopy = seasonalProgressCopy(collection);

    return (
        <div className={styles.card} onClick={onClick}>
            <div className={styles.cardHeader}>
                <h3>{collection.name}</h3>
                <div className={styles.rewardInfo}>
                    {/* Grid: Signature rewards show their static poster, never video */}
                    {resolveCoinMedia(collection.reward).posterUrl && (
                        <div onClick={(e) => e.stopPropagation()}>
                            <ProgressiveCoinReveal
                                coinImageUrl={resolveCoinMedia(collection.reward).posterUrl}
                                coinName={collection.reward.name}
                                verified={towardRequired}
                                required={required}
                                size={44}
                                bgColor="var(--color-bg-secondary, #1a1a1a)"
                                nextTitle={nextTitle}
                            />
                        </div>
                    )}
                    <span className={styles.rewardName}>💎 {collection.reward?.name}</span>
                    <SignatureBadge coin={collection.reward} />
                    {collection.completed && (
                        <span className={styles.legendaryBadge}>LEGENDARY</span>
                    )}
                    <SeasonalBadge collection={collection} />
                    {requirement && <span className={styles.requirementPill}>{requirement}</span>}
                    <CollectionCardCollectors collection={collection} />
                </div>
            </div>

            <div className={styles.postersRow}>
                {previewPosters.map((poster, idx) => (
                    <img
                        key={idx}
                        src={poster.posterUrl}
                        alt={poster.title}
                        className={styles.posterThumb}
                    />
                ))}
                {collection.items.length > previewPosters.length && (
                    <div className={styles.posterMore}>
                        +{collection.total - previewPosters.length}
                    </div>
                )}
            </div>

            <div className={styles.progressInfo}>
                <span className={styles.progressText}>
                    {progressLabel(collection)}
                </span>
                {seasonalCopy ? (
                    <span className={styles.seasonalNote}>{seasonalCopy}</span>
                ) : !collection.completed ? (
                    <span className={styles.remainingText}>
                        {collection.remaining} more to unlock 💎
                    </span>
                ) : (
                    <span className={styles.completedText}>✓ Collection Complete</span>
                )}
            </div>

            <div className={styles.progressBarBg}>
                <div
                    className={`${styles.progressBarFill} ${collection.completed ? styles.completed : ''}`}
                    style={{ width: `${progressPercent}%` }}
                />
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Logged-OUT branch — public card grid
───────────────────────────────────────────────────────────────────────────── */
function PublicCollections() {
    const [collections, setCollections] = useState([]);
    const [loading, setLoading]         = useState(true);

    useEffect(() => {
        listPublicCollections()
            .then((res) => setCollections(Array.isArray(res) ? res : []))
            .catch((err) => console.error('Failed to load public collections:', err))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.container}>
                    <p className={styles.loading}>Loading collections…</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                <h1 className={styles.title}>Legendary Collections</h1>
                <p className={styles.subtitle}>
                    Complete collections to earn exclusive Legendary coins
                </p>

                {/* ── Signup banner ── */}
                <div className={styles.signupBanner}>
                    <span>Create a free account to track your progress</span>
                    <Link to="/register" className={styles.signupBannerBtn}>
                        Sign up free
                    </Link>
                </div>

                {/* ── Public card grid ── */}
                {collections.length === 0 ? (
                    <p className={styles.emptyText}>No collections available yet.</p>
                ) : (
                    <div className={styles.grid}>
                        {collections.map((c) => (
                            <PublicCollectionCard key={c.id} collection={c} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function PublicCollectionCard({ collection }) {
    return (
        <Link to={`/collections/${collection.slug}`} className={styles.cardLink}>
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h3>{collection.name}</h3>
                    <div className={styles.rewardInfo}>
                        {resolveCoinMedia(collection.reward).posterUrl && (
                            <img
                                src={resolveCoinMedia(collection.reward).posterUrl}
                                alt={`${collection.reward.name} coin`}
                                className={styles.publicCoinThumb}
                            />
                        )}
                        <span className={styles.rewardName}>💎 {collection.reward?.name}</span>
                        <SignatureBadge coin={collection.reward} />
                        <SeasonalBadge collection={collection} />
                        {/*<span className={styles.legendaryBadge}>LEGENDARY</span>*/}
                    </div>
                </div>

                {collection.description && (
                    <p className={styles.publicDescription}>{collection.description}</p>
                )}

                {/* Poster strip */}
                {collection.posterUrls?.length > 0 && (
                    <div className={styles.postersRow}>
                        {collection.posterUrls.slice(0, 5).map((url, idx) => (
                            <img
                                key={idx}
                                src={url}
                                alt=""
                                className={styles.posterThumb}
                            />
                        ))}
                        {collection.total > collection.posterUrls.slice(0, 5).length && (
                            <div className={styles.posterMore}>
                                +{collection.total - collection.posterUrls.slice(0, 5).length}
                            </div>
                        )}
                    </div>
                )}

                <p className={styles.publicTotalBadge}>
                    {collection.total} {collection.total === 1 ? 'title' : 'titles'}
                    <CollectionCardCollectors collection={collection} className={styles.publicCollectors} />
                </p>
            </div>
        </Link>
    );
}
